/**
 * Takip Dashboard — kapsamlı görev/to-do list takip alanı.
 *
 * Kaynak: `GET /api/tasks/tracking?days=N`. Tek round-trip'te
 * gösterilen bölümler:
 *   • Toplam KPI'lar (açık / tamamlanmış / gecikmiş / son N gün velocity)
 *   • Haftalık tamamlama trendi (mini area chart)
 *   • Proje bazlı ilerleme (tamamlanma yüzdesi + gecikmiş sayacı)
 *   • Kişi bazlı takip (sıralanabilir sağ tablo)
 *   • En hızlı yapanlar (avg saat)
 *   • En çok geciktirenler (avg gecikme saati)
 *   • Son tamamlananlar / son eklenenler / gecikmiş öncelikliler
 *
 * Mobil: kartlar tek kolon, tablolar horizontal scroll'lu.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Loader2,
  RefreshCw,
  Trophy,
  Flame,
  AlertTriangle,
  CheckCircle2,
  ListChecks,
  Users as UsersIcon,
  Clock,
  TrendingUp,
  ChevronRight,
  Zap,
  Rocket,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { useAuthStore } from "../store/authStore";

const WINDOWS = [
  { label: "7 gün", value: 7 },
  { label: "30 gün", value: 30 },
  { label: "90 gün", value: 90 },
];

function humanHours(h) {
  if (h == null || Number.isNaN(h)) return "—";
  if (h < 1) return `${Math.round(h * 60)}dk`;
  if (h < 48) return `${h.toFixed(1)}sa`;
  const d = h / 24;
  if (d < 14) return `${d.toFixed(1)}g`;
  const w = d / 7;
  return `${w.toFixed(1)}h`;
}

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "az önce";
  if (diff < 3600) return `${Math.floor(diff / 60)}dk önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}sa önce`;
  const days = Math.floor(diff / 86400);
  if (days < 30) return `${days}g önce`;
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
}

function initials(name) {
  return (name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function TakipDashboard() {
  const { user: me } = useAuthStore();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/tasks/tracking", { params: { days } });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, [days]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }
  if (!data) return null;

  const t = data.totals || {};
  const openPct = t.total ? Math.round((t.open / t.total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="takip-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
            Serpix
          </div>
          <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            <ListChecks className="h-7 w-7 text-violet-600" strokeWidth={1.75} />
            Takip Dashboard
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-zinc-500">
            Tüm to-do listelerin, projelerin ve ekip performansının canlı özeti.
            {me?.role === "user" && " (yalnızca senin görebildiğin görevler)"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="inline-flex overflow-hidden rounded-lg border border-zinc-200 bg-white text-xs"
            data-testid="takip-window-toggle"
          >
            {WINDOWS.map((w) => (
              <button
                key={w.value}
                type="button"
                onClick={() => setDays(w.value)}
                data-testid={`takip-window-${w.value}`}
                className={[
                  "px-3 py-1.5 font-medium transition-colors",
                  days === w.value
                    ? "bg-zinc-900 text-white"
                    : "text-zinc-600 hover:bg-zinc-100",
                ].join(" ")}
              >
                {w.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={load}
            disabled={refreshing}
            data-testid="takip-refresh"
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Yenile</span>
          </Button>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KPI
          testId="kpi-total"
          icon={ListChecks}
          label="Toplam iş"
          value={t.total}
          hint={`${openPct}% açık`}
          tone="slate"
        />
        <KPI
          testId="kpi-open"
          icon={Clock}
          label="Açık"
          value={t.open}
          hint="tamamlanmamış"
          tone="blue"
        />
        <KPI
          testId="kpi-done"
          icon={CheckCircle2}
          label="Tamamlanan"
          value={t.done}
          hint={`son ${t.days_window || days}g: +${t.done_last_days || 0}`}
          tone="green"
        />
        <KPI
          testId="kpi-overdue"
          icon={AlertTriangle}
          label="Gecikmiş"
          value={t.overdue}
          hint={t.overdue > 0 ? "acil müdahale gerekli" : "temiz"}
          tone={t.overdue > 0 ? "red" : "slate"}
        />
      </div>

      {/* Trend + progress overview */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <TrendCard trend={data.weekly_trend || []} totals={t} />
        <FastestCard users={data.fastest_users || []} />
        <SlowestCard users={data.slowest_users || []} />
      </div>

      {/* Per-project + per-user */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ProjectBreakdown rows={data.by_project || []} />
        <UserBreakdown rows={data.by_user || []} />
      </div>

      {/* Recent lists */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <RecentList
          testId="recent-completions"
          title="Son tamamlananlar"
          icon={CheckCircle2}
          items={data.recent_completions || []}
          emptyLabel="Henüz tamamlanan görev yok"
          timeField="updated_at"
          tone="green"
        />
        <RecentList
          testId="top-overdue"
          title="En kritik gecikmiş"
          icon={Flame}
          items={data.top_overdue || []}
          emptyLabel="Gecikmiş görev yok"
          timeField="due_at"
          tone="red"
          showOverdueDelta
        />
        <RecentList
          testId="recent-creations"
          title="Son eklenen görevler"
          icon={Rocket}
          items={data.recent_creations || []}
          emptyLabel="Henüz görev yok"
          timeField="created_at"
          tone="violet"
        />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// KPI card
// -----------------------------------------------------------------------------
const TONE = {
  slate: { bg: "bg-white", border: "border-zinc-200", ic: "bg-zinc-100 text-zinc-700", val: "text-zinc-900" },
  blue: { bg: "bg-gradient-to-br from-sky-50 to-white", border: "border-sky-200", ic: "bg-sky-100 text-sky-700", val: "text-sky-950" },
  green: { bg: "bg-gradient-to-br from-emerald-50 to-white", border: "border-emerald-200", ic: "bg-emerald-100 text-emerald-700", val: "text-emerald-950" },
  red: { bg: "bg-gradient-to-br from-rose-50 to-white", border: "border-rose-200", ic: "bg-rose-100 text-rose-700", val: "text-rose-950" },
  violet: { bg: "bg-gradient-to-br from-violet-50 to-white", border: "border-violet-200", ic: "bg-violet-100 text-violet-700", val: "text-violet-950" },
};

function KPI({ testId, icon: Icon, label, value, hint, tone = "slate" }) {
  const T = TONE[tone] || TONE.slate;
  return (
    <div
      data-testid={testId}
      className={`flex items-center gap-3 rounded-xl border ${T.border} ${T.bg} p-3 sm:p-4`}
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${T.ic}`}>
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
        <div className={`text-2xl font-semibold leading-tight ${T.val}`}>
          {value ?? 0}
        </div>
        {hint && (
          <div className="mt-0.5 truncate text-[11px] text-zinc-500">{hint}</div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Weekly trend card
// -----------------------------------------------------------------------------
function TrendCard({ trend, totals }) {
  const data = useMemo(
    () =>
      (trend || []).map((row) => ({
        date: row.date.slice(5), // MM-DD
        count: row.count,
      })),
    [trend],
  );
  const sum7 = data.reduce((a, b) => a + b.count, 0);
  return (
    <div
      data-testid="trend-card"
      className="rounded-xl border border-zinc-200 bg-white p-4 lg:col-span-1"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">
            Son 7 gün
          </div>
          <div className="mt-0.5 text-lg font-semibold text-zinc-900">
            {sum7} tamamlandı
          </div>
        </div>
        <div className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
          <TrendingUp className="h-3 w-3" />
          {totals.completion_pct}% oran
        </div>
      </div>
      <div className="mt-3 h-32">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71717a" }} axisLine={false} tickLine={false} />
            <YAxis hide domain={[0, "dataMax + 1"]} allowDecimals={false} />
            <RTooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e4e4e7" }}
              labelStyle={{ fontSize: 11, color: "#71717a" }}
              formatter={(v) => [v, "Tamamlandı"]}
            />
            <Area type="monotone" dataKey="count" stroke="#8b5cf6" strokeWidth={2} fill="url(#trendFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Fastest & Slowest cards
// -----------------------------------------------------------------------------
function FastestCard({ users }) {
  return (
    <div
      data-testid="fastest-card"
      className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4"
    >
      <div className="mb-2 flex items-center gap-2 text-emerald-800">
        <Trophy className="h-4 w-4" strokeWidth={2} />
        <span className="text-sm font-semibold">En hızlı yapanlar</span>
      </div>
      {users.length === 0 ? (
        <EmptyRow label="Henüz veri yok" />
      ) : (
        <ul className="space-y-1.5">
          {users.map((u, i) => (
            <li
              key={u.user_id}
              data-testid={`fastest-row-${u.user_id}`}
              className="flex items-center gap-2 rounded-md bg-white/60 px-2 py-1.5 text-sm"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-zinc-900">{u.name}</div>
                <div className="text-[10px] text-zinc-500">
                  {u.completed_count} görev · ort {humanHours(u.avg_hours)}
                </div>
              </div>
              <Zap className="h-3.5 w-3.5 text-emerald-500" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SlowestCard({ users }) {
  return (
    <div
      data-testid="slowest-card"
      className="rounded-xl border border-rose-200 bg-gradient-to-br from-rose-50 to-white p-4"
    >
      <div className="mb-2 flex items-center gap-2 text-rose-800">
        <Flame className="h-4 w-4" strokeWidth={2} />
        <span className="text-sm font-semibold">En çok geciktirenler</span>
      </div>
      {users.length === 0 ? (
        <EmptyRow label="Hiç geciken kimse yok 🎉" />
      ) : (
        <ul className="space-y-1.5">
          {users.map((u, i) => (
            <li
              key={u.user_id}
              data-testid={`slowest-row-${u.user_id}`}
              className="flex items-center gap-2 rounded-md bg-white/60 px-2 py-1.5 text-sm"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-600 text-[10px] font-bold text-white">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-zinc-900">{u.name}</div>
                <div className="text-[10px] text-zinc-500">
                  {u.overdue_count} gecikmiş · ort {humanHours(u.avg_delay_hours)} gecikme
                </div>
              </div>
              <AlertTriangle className="h-3.5 w-3.5 text-rose-500" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Per-project breakdown (progress rows)
// -----------------------------------------------------------------------------
function ProjectBreakdown({ rows }) {
  return (
    <div
      data-testid="by-project-card"
      className="rounded-xl border border-zinc-200 bg-white p-4"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-zinc-500" />
          <span className="text-sm font-semibold text-zinc-900">Proje bazlı takip</span>
        </div>
        <span className="text-[11px] text-zinc-500">{rows.length} proje</span>
      </div>
      {rows.length === 0 ? (
        <EmptyRow label="Aktif proje yok" />
      ) : (
        <ul className="space-y-2.5">
          {rows.slice(0, 8).map((p) => (
            <li
              key={p.project_id}
              data-testid={`by-project-row-${p.project_id}`}
              className="space-y-1.5"
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: p.color }}
                />
                <Link
                  to={`/app/projects/${p.project_id}`}
                  className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-800 hover:text-violet-700 hover:underline"
                >
                  {p.name}
                </Link>
                <span className="text-[11px] text-zinc-500">{p.completion_pct}%</span>
                {p.overdue > 0 && (
                  <span
                    className="inline-flex items-center gap-0.5 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-700"
                    title={`${p.overdue} gecikmiş`}
                  >
                    <AlertTriangle className="h-2.5 w-2.5" />
                    {p.overdue}
                  </span>
                )}
              </div>
              <Progress value={p.completion_pct} className="h-1.5" />
              <div className="flex items-center justify-between text-[10px] text-zinc-500">
                <span>{p.done} tamam · {p.open} açık · {p.total} toplam</span>
                <Link
                  to={`/app/projects/${p.project_id}`}
                  className="inline-flex items-center gap-0.5 text-violet-600 hover:underline"
                >
                  Aç <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Per-user breakdown table
// -----------------------------------------------------------------------------
function UserBreakdown({ rows }) {
  const [sortKey, setSortKey] = useState("open");
  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      if (sortKey === "name") return (a.name || "").localeCompare(b.name || "");
      return (b[sortKey] || 0) - (a[sortKey] || 0);
    });
    return arr;
  }, [rows, sortKey]);

  return (
    <div
      data-testid="by-user-card"
      className="overflow-hidden rounded-xl border border-zinc-200 bg-white"
    >
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <UsersIcon className="h-4 w-4 text-zinc-500" />
          <span className="text-sm font-semibold text-zinc-900">Kişi bazlı takip</span>
        </div>
        <span className="text-[11px] text-zinc-500">{rows.length} kişi</span>
      </div>
      {sorted.length === 0 ? (
        <div className="px-4 pb-4">
          <EmptyRow label="Atanmış görev yok" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th
                  className="cursor-pointer px-4 py-2 text-left font-medium hover:text-zinc-800"
                  onClick={() => setSortKey("name")}
                  data-testid="user-sort-name"
                >
                  Kişi
                </th>
                <th
                  className="cursor-pointer px-3 py-2 text-right font-medium hover:text-zinc-800"
                  onClick={() => setSortKey("open")}
                  data-testid="user-sort-open"
                >
                  Açık
                </th>
                <th
                  className="cursor-pointer px-3 py-2 text-right font-medium hover:text-zinc-800"
                  onClick={() => setSortKey("done")}
                  data-testid="user-sort-done"
                >
                  Tamam
                </th>
                <th
                  className="cursor-pointer px-3 py-2 text-right font-medium hover:text-zinc-800"
                  onClick={() => setSortKey("overdue")}
                  data-testid="user-sort-overdue"
                >
                  Geciken
                </th>
                <th className="px-3 py-2 text-right font-medium">%</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {sorted.map((u) => (
                <tr
                  key={u.user_id}
                  data-testid={`by-user-row-${u.user_id}`}
                  className="hover:bg-zinc-50"
                >
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-[9px] font-semibold text-white">
                        {initials(u.name)}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-[13px] font-medium text-zinc-900">
                          {u.name}
                        </div>
                        <div className="truncate text-[10px] text-zinc-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-700">{u.open}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{u.done}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${u.overdue ? "text-rose-700 font-semibold" : "text-zinc-400"}`}>
                    {u.overdue}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <span className="tabular-nums text-[11px] text-zinc-500">
                        {u.completion_pct}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Recent list card
// -----------------------------------------------------------------------------
const RECENT_TONE = {
  green: "border-emerald-200",
  red: "border-rose-200",
  violet: "border-violet-200",
  slate: "border-zinc-200",
};

function RecentList({
  testId,
  title,
  icon: Icon,
  items,
  emptyLabel,
  timeField,
  tone = "slate",
  showOverdueDelta,
}) {
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border ${RECENT_TONE[tone]} bg-white p-4`}
    >
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-zinc-500" />
        <span className="text-sm font-semibold text-zinc-900">{title}</span>
      </div>
      {items.length === 0 ? (
        <EmptyRow label={emptyLabel} />
      ) : (
        <ul className="space-y-1.5">
          {items.map((it) => {
            const delta = showOverdueDelta && it.due_at
              ? Math.max(0, (Date.now() - new Date(it.due_at).getTime()) / 3600000)
              : null;
            return (
              <li
                key={it.id}
                data-testid={`${testId}-row-${it.id}`}
                className="flex items-start gap-2 rounded-md bg-zinc-50/60 px-2 py-1.5 text-sm"
              >
                <Link
                  to={`/app/projects/${it.project_id}?task=${it.id}`}
                  className="min-w-0 flex-1 truncate text-zinc-800 hover:text-violet-700 hover:underline"
                  title={it.title}
                >
                  {it.title}
                </Link>
                <div className="flex shrink-0 flex-col items-end text-[10px] text-zinc-500">
                  {it.project_name && (
                    <span className="truncate max-w-[100px]">{it.project_name}</span>
                  )}
                  <span>
                    {delta != null
                      ? `+${humanHours(delta)} geç`
                      : relTime(it[timeField])}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function EmptyRow({ label }) {
  return (
    <div className="rounded-md border border-dashed border-zinc-200 py-4 text-center text-xs text-zinc-400">
      {label}
    </div>
  );
}
